import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FwButton, FwDataTable, FwPagination, ToastController } from '@freshworks/crayons/react';
import { LEGO_CONSTANTS, SERVICE_TYPE, PERMISSION_TYPE, MESSAGE_CHANNEL_COMM_TYPE, SCHEMA_FIELD_OPERATIONS } from 'constants/index';
import getService from 'services/serviceDiscovery';
import { useFreshdeskContextState, useNativeObjectsUpdater } from '../../store';
import { DeleteModal } from 'UI';
import { CommonUtils, getEntityRouteParam } from 'utils';
import FormBuildController from 'routes/create/formbuilder/FormBuildController';
import EntityBasicDetails from 'components/EntityBuilder/BasicDetails/EntityBasicDetails';
import EntityListHeader from './EntityListHeader/EntityListHeader';
import styles from './EntityList.module.css';
import BlankState from 'UI/BlankState/BlankState';
import emptyCustomObjectImage from '../../images/empty-custom-objects.svg';
import { InteractivityBeacon } from 'react-analytics-collector';
import { useMessageChannelAdapter, useEntityTableColumns, useInjectEntitiesSchemaPayload } from 'hooks';
import { useTheme } from 'store/theme-wrapper';

export default function EntityList({ entities, fetchEntities, error, idLookup, isLoading, paginationInfo, onPageChange }) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { currentThemeClass } = useTheme();
  const [entity, setEntity] = useState(null);
  const deleteModalRef = useRef(null);
  const toast = ToastController();
  const navigate = useNavigate();
  const { getSchemaPermissions, getRecordPermissions, getUserDetails, getCollections } = useFreshdeskContextState();
  const { loadNativeObjectsData } = useNativeObjectsUpdater();
  const entityPermissions = getSchemaPermissions();
  const recordPermissions = getRecordPermissions();
  const language = getUserDetails()?.locale?.language;
  const timezone = getUserDetails()?.locale?.timezone;
  const { onSchemaCRUD } = useMessageChannelAdapter();
  const injectEntitiesSchemaPayload = useInjectEntitiesSchemaPayload();
  const jsonPreset = FormBuildController.getInstance().jsonPresetSchema;
  const { t } = useTranslation();
  FormBuildController.getInstance().i18nTranslate = t;
  FormBuildController.getInstance().collections = getCollections();

  const iconSrc = FormBuildController.getInstance().iconCDNUrl;
  const relatedEntitiesMap = entities?.reduce((obj, entity) => {
    const next = { ...obj, [entity.id]: entity.name };
    if (entity.type != null) {
      next[entity.type] = entity.name;
    }
    return next;
  }, {});

  const ENTITY_COLUMN = useEntityTableColumns();

  const openDeleteModalHandler = entity => {
    setEntity(entity);
    deleteModalRef?.current?.open();
  };

  const onDeleteEntityHandler = async id => {
    const service = getService(SERVICE_TYPE.ENTITY);
    service
      .delete({ id })
      .then(() => {
        const context = { action: MESSAGE_CHANNEL_COMM_TYPE.DELETED, resource: MESSAGE_CHANNEL_COMM_TYPE.RESOURCES.SCHEMA, parameters: { id } };
        onSchemaCRUD(context);
        deleteModalRef?.current?.close();
        toast.trigger({
          type: 'success',
          content: t('entities.deleteCustomObjectWithSuccess'),
        });
        setEntity(null);
        fetchEntities();
      })
      .catch(({ data: { message, errors } }) => {
        if (errors && errors[0].message === LEGO_CONSTANTS.DELETE_CUSTOM_OBJECTS_WITH_RECORDS_ERROR) {
          deleteModalRef?.current?.close();
          toast.trigger({
            type: 'error',
            content: t('entities.deleteCustomObjectWithRecordsError'),
          });
          setEntity(null);
        } else {
          deleteModalRef?.current?.close();
          toast.trigger({
            type: 'error',
            content: message ?? t('entities.deleteCustomObjectWithResponseFailure'),
          });
          setEntity(null);
        }
      });
  };

  const getAssociatedObjectsList = objects => {
    if (!objects) return LEGO_CONSTANTS.EMPTY_DEFAULT_DATA;
    const associatedObjectsArray = objects
      .filter(object => object.type === LEGO_CONSTANTS.RELATIONSHIP)
      ?.map(object => relatedEntitiesMap[object.related_entity_id] ?? idLookup[object.related_entity_id]?.name)
      ?.filter(Boolean);
    const associatedObjects = [...new Set(associatedObjectsArray)]?.join(', ');
    return associatedObjects.length === 0 ? LEGO_CONSTANTS.EMPTY_DEFAULT_DATA : associatedObjects;
  };

  const ModalDescription = ({ entity }) => t('entities.deleteEntityModalDescriptionWithNoAssociatedObjects', { entityName: entity?.title ?? entity?.name?.text });
  const getDisplayIdWiseEntityData = entities => {
    const sortedByCreatedAtEntities = entities?.sort((a, b) => b.created_time - a.created_time);
    return sortedByCreatedAtEntities?.map(entity => {
      const routeParam = getEntityRouteParam(entity);
      const iconName = CommonUtils.getIconNamefromUrl(entity?.icon_link);
      const iconColor = CommonUtils.generateThemeBasedIconColor(entity?.icon_color, LEGO_CONSTANTS.THEME[currentThemeClass]);
      const rowInfo = {
        ...entity,
        routeParam,
        created_time: CommonUtils.timeSince(entity.created_time, t, language ?? LEGO_CONSTANTS.US_TIME_ZONE, timezone),
        icon: { name: iconName || jsonPreset.iconSet?.[0].name || '', src: iconSrc, size: 18, color: iconColor },
        associated_objects: getAssociatedObjectsList(entity?.fields),
      };
      rowInfo.name = {
        text: rowInfo.name,
        href: `${LEGO_CONSTANTS.ROUTE_PREFIX}/schemas/edit/${routeParam}`,
      };
      return rowInfo;
    });
  };

  const getRowActions = () => {
    const ROW_ACTIONS = [];

    entityPermissions.isPresent(PERMISSION_TYPE.UPDATE) &&
      ROW_ACTIONS.push({
        name: t('entities.editObject'),
        iconName: 'edit',
        handler: row => navigate(`${LEGO_CONSTANTS.ROUTE_PREFIX}/schemas/edit/${row.routeParam}`),
      });
    entityPermissions.isPresent(PERMISSION_TYPE.READ) &&
      recordPermissions.isPresent(PERMISSION_TYPE.READ) &&
      ROW_ACTIONS.push({
        name: t('entities.viewRecords'),
        iconName: 'visible',
        handler: row => navigate(`${LEGO_CONSTANTS.ROUTE_PREFIX}/schemas/${row.routeParam}/records`),
      });
    entityPermissions.isPresent(PERMISSION_TYPE.DELETE) &&
      ROW_ACTIONS.push({
        name: t('entities.deleteObject'),
        iconName: 'delete',
        handler: entity => openDeleteModalHandler(entity),
      });

    return ROW_ACTIONS;
  };

  const onCreateNewObjectHandler = () => {
    setShowCreateModal(true);
  };

  const onSubmitBasicDetailsHandler = async objDetail => {
    // Create new custom object
    let objPayload = FormBuildController.getInstance().getNewEntityPayload(objDetail);
    objPayload = await injectEntitiesSchemaPayload(objPayload, SCHEMA_FIELD_OPERATIONS.CREATE, objDetail);
    const objApiResponse = await FormBuildController.getInstance().createNewEntity(objPayload);
    const boolSuccess = objApiResponse?.success;
    setShowCreateModal(false);

    if (boolSuccess) {
      toast.trigger({
        type: 'success',
        content: t('createEntity.toast.newEntity.success'),
      });
      const context = {
        action: MESSAGE_CHANNEL_COMM_TYPE.CREATED,
        resource: MESSAGE_CHANNEL_COMM_TYPE.RESOURCES.SCHEMA,
        parameters: { id: objApiResponse.response.id },
      };
      onSchemaCRUD(context);
      loadNativeObjectsData();
      const createdEntity = FormBuildController.getInstance().jsonApiResponse;
      const editRouteParam = getEntityRouteParam(createdEntity) ?? objApiResponse.response?.id;
      navigate(`${LEGO_CONSTANTS.ROUTE_PREFIX}/schemas/edit/${editRouteParam}`);
    } else {
      toast.trigger({
        type: 'error',
        content: objApiResponse.errorMessage,
      });
    }
  };

  const onCloseBasicDetailsHandler = () => {
    setShowCreateModal(false);
  };

  const ROW_ACTIONS = getRowActions();

  useEffect(() => {
    FormBuildController.getInstance().destroy();
  }, []);

  const areEntitiesPresent = entities && entities.length > 0;
  const showPagination = Boolean(paginationInfo?.total && paginationInfo?.perPage && paginationInfo.total > paginationInfo.perPage);

  const blankHeaderDetails = error => {
    if (error) {
      if (error?.name === LEGO_CONSTANTS.ERRORS.PERMISSION) {
        return {
          heading: t('entities.error.permissionErrorMessage'),
          description: '',
        };
      } else if (error?.error_type === LEGO_CONSTANTS.RESOURCE_NOT_FOUND && error?.message === LEGO_CONSTANTS.TENANT_NOT_FOUND) {
        return {
          heading: t('entities.customObjects'),
          description: t('entities.createCustomObjectDescription'),
        };
      } else {
        return {
          header: error?.message,
          description: '',
        };
      }
    }
    return {
      heading: t('entities.customObjects'),
      description: t('entities.createCustomObjectDescription'),
    };
  };

  return (
    <>
      {areEntitiesPresent ? (
        <>
          <EntityListHeader onCreateNewObjectHandler={onCreateNewObjectHandler} />
          <div className={styles['entities-container']}>
            <InteractivityBeacon />
            {showPagination && (
              <div className={styles['pagination-container']}>
                <FwPagination
                  page={paginationInfo.page}
                  perPage={paginationInfo.perPage}
                  total={paginationInfo.total}
                  isLoading={isLoading}
                  onFwChange={e => onPageChange?.(e.detail?.page)}
                />
              </div>
            )}
            <FwDataTable columns={ENTITY_COLUMN} rowActions={ROW_ACTIONS} rows={getDisplayIdWiseEntityData(entities)} label={t('entities.customObjects')} />
          </div>
          <DeleteModal
            ref={deleteModalRef}
            onDeleteHandler={e => {
              e.preventDefault();
              onDeleteEntityHandler(entity?.routeParam ?? getEntityRouteParam(entity));
            }}
            heading={`${t('entities.deleteEntityModalHeading', { entityName: entity?.title ?? entity?.name?.text })}`}
            description={<ModalDescription entity={entity} />}
          />
        </>
      ) : (
        <>
          <InteractivityBeacon />
          <BlankState
            heading={blankHeaderDetails(error).heading}
            description={blankHeaderDetails(error).description}
            showActions={entities !== null}
            singleAction={true}
            image={emptyCustomObjectImage}
            blankContainer={true}
          >
            <FwButton onFwClick={onCreateNewObjectHandler} color="primary">
              {t('entities.createObject')}
            </FwButton>
          </BlankState>
        </>
      )}
      {showCreateModal && (
        <EntityBasicDetails
          iconPrefixUrl={FormBuildController.getInstance().iconCDNUrl}
          entitySchema={{ icon_link: jsonPreset?.iconSet?.[0].name }}
          isSlider={false}
          jsonPreset={jsonPreset}
          isOpen={showCreateModal}
          titleText={t('createEntity.basicDetailsCreateHeader')}
          submitText={t('createEntity.basicDetailsCreateEntityBtn')}
          cancelText={t('createEntity.basicDetailsCancelBtn')}
          onSubmit={onSubmitBasicDetailsHandler}
          onClose={onCloseBasicDetailsHandler}
        />
      )}
    </>
  );
}

EntityList.defaultProps = {
  entities: [],
};
